import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import socketio from "socket.io-client";
import { SOCKET_URL } from "lib/constants";
import { getAuthTokens } from "lib/helpers";
import { useAuth } from "./AuthContext";
import { SOCKET_EVENTS } from "lib/constants";
import { useDispatch } from "react-redux";
import { setHasNewNotifications } from "store/slices/notifications";
import { setUpdateNotifications } from "store/slices/notifications";
import { useHistory } from "react-router-dom";
import { useAuthSelector } from "store/slices/auth";
import { loadNotificationsCountThunk } from "store/slices/notifications";
import { setNotificationsCount } from "store/slices/notifications";
import { useNotificationsCountSelector } from "store/slices/notifications";
import { useNotificationTypeSelector } from "store/slices/notifications";
import { fetchNotificationsThunk } from "store/slices/notifications";

const SocketContext = React.createContext();

export function SocketProvider({ children }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const notificationType = useNotificationTypeSelector();
  const [isConnected, setIsConnected] = useState(false);

  const socketRef = useRef(null); // ✅ Use ref for stable socket
  const currentTokenRef = useRef(null); // ✅ Track token in ref
  const isInitializingRef = useRef(false); // ✅ Prevent duplicate initialization

  const auth = useAuth();
  const authState = useAuthSelector();
  const user = authState?.user?.data?.user;

  const notificationsCount = useNotificationsCountSelector();

  // ✅ Create/update socket only when authenticated
  useEffect(() => {
    if (!auth?.isAuthenticated) {
      console.log("Not authenticated, skipping socket connection");
      // Clean up socket if user logs out
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        currentTokenRef.current = null;
      }
      return;
    }

    const accessToken = getAuthTokens()?.accessToken;

    if (!accessToken) {
      console.log("No access token found");
      return;
    }

    // ✅ Only reconnect if token actually changed
    if (
      accessToken === currentTokenRef.current &&
      socketRef.current?.connected
    ) {
      console.log(
        "✅ Socket already connected with same token, reusing existing connection"
      );
      return;
    }

    // ✅ Prevent duplicate initialization
    if (isInitializingRef.current) {
      console.log("⚠️ Socket initialization already in progress...");
      return;
    }

    console.log("🔌 Creating new socket connection...");
    isInitializingRef.current = true;

    // Disconnect old socket if exists
    if (socketRef.current) {
      console.log("🔌 Disconnecting old socket before creating new one");
      socketRef.current.removeAllListeners(); // Remove all listeners first
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Create new socket
    const newSocket = socketio.connect(process.env.REACT_APP_SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      query: { token: accessToken },
      transports: ["websocket", "polling"],
      forceNew: true, // ✅ Force new connection
    });

    newSocket.on("connect", () => {
      console.log("✅ Connected to socket server:", newSocket.id);
      setIsConnected(true);
      isInitializingRef.current = false;
    });

    newSocket.on("disconnect", (reason) => {
      console.log("❌ Disconnected from socket server:", reason);
      setIsConnected(false);

      // ✅ Don't auto-reconnect if it's a manual disconnect
      if (reason === "io client disconnect") {
        console.log("Manual disconnect, not reconnecting");
        return;
      }
    });

    newSocket.on("connect_error", (error) => {
      console.error("🔴 Socket connection error:", error.message);
      isInitializingRef.current = false;
    });

    newSocket.on("error", (error) => {
      console.log("🔴 Socket error:", error);
    });

    // Update refs
    socketRef.current = newSocket;
    currentTokenRef.current = accessToken;

    // ✅ Cleanup only on unmount or auth change
    return () => {
      console.log("🧹 Cleaning up socket connection");
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }
      isInitializingRef.current = false;
    };
  }, [auth?.isAuthenticated]); // ✅ Only depend on auth status

  useEffect(() => {
    const isAllowedPath = !["/auth/welcome", "/auth/welcome/"].includes(
      history.location.pathname
    );
    if (user && isAllowedPath && auth?.refreshToken) {
      dispatch(loadNotificationsCountThunk({}));
    }
  }, [history.location.pathname, auth?.refreshToken]);

  useEffect(() => {
    if (auth?.isAuthenticated) {
      dispatch(fetchNotificationsThunk({ notificationType: "all", page: 1 }));
    }
  }, [auth?.isAuthenticated]);

  // ✅ Handle notification events
  useEffect(() => {
    if (!socketRef.current) return;

    const handleReceiveNotification = (notification) => {
      console.log("📬 Notification received:", notification);
      if (notificationType !== "read") {
        dispatch(setUpdateNotifications(notification));
      }
      const newCount = notificationsCount + 1;
      dispatch(setNotificationsCount({ count: newCount }));
      if (
        !["/admin/notifications", "/admin/notifications/"].includes(
          history.location.pathname
        )
      ) {
        dispatch(setHasNewNotifications(true));
      }
    };

    socketRef.current.on(
      SOCKET_EVENTS.RECEIVE_NOTIFICATIONS,
      handleReceiveNotification
    );

    // ✅ Clean up listener only
    return () => {
      if (socketRef.current) {
        socketRef.current.off(
          SOCKET_EVENTS.RECEIVE_NOTIFICATIONS,
          handleReceiveNotification
        );
      }
    };
  }, [
    notificationsCount,
    notificationType,
    history.location.pathname,
    dispatch,
  ]);

  // ✅ Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      socket: socketRef.current,
      isConnected,
    }),
    [isConnected]
  ); // Only recreate when connection status changes

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocketContext() {
  const socket = useContext(SocketContext);
  return socket;
}

// // ✅ Export for debugging in console
// if (typeof window !== 'undefined') {
//   window.getSocketDebug = () => ({
//     socketExists: !!socketRef?.current,
//     socketId: socketRef?.current?.id,
//     connected: socketRef?.current?.connected,
//   });
// }
